import React from 'react'

import { Dropdown, useModalContext } from '@sketch/components'
import { useFlag, useFolderTerm } from '@sketch/modules-common'
import { WorkspaceMinimalFragment, ProjectFragment } from '@sketch/gql-types'

import {
  CopyProjectAction,
  DeleteProjectAction,
  LeaveProjectAction,
  EditProjectAction,
  RenameProjectAction,
  ShareProjectAction,
} from '../ProjectDropdownActions'

import UnarchiveProjectModal from '../../modals/UnarchiveProjectModal'
import { checkCommonPermissionChecks } from './checkCommonPermissionChecks'
import { isTruthy } from '@sketch/utils'
import { MoveStorageItemModal } from '../../../storageItems'

export interface ProjectArchivedDropdownProps {
  workspace: WorkspaceMinimalFragment
  project: ProjectFragment
  onRename?: () => void
}

export const ProjectArchivedDropdown = (
  props: ProjectArchivedDropdownProps
) => {
  const { workspace, project, onRename } = props
  const workspaceId = workspace.identifier

  const { showModal } = useModalContext()

  const isNestedProjectsOn = useFlag('nested-projects')

  const { showDivider, showEditActions, showLeaveAction } =
    checkCommonPermissionChecks(props)
  const showShareAction = showEditActions && project.projectType === 'STANDARD'

  const folderTerm = useFolderTerm()

  return (
    <>
      <Dropdown.Header>{folderTerm.upper}</Dropdown.Header>
      {showShareAction && (
        <ShareProjectAction project={project} workspace={workspace} />
      )}
      <CopyProjectAction project={project} workspaceId={workspaceId} />
      {[
        showEditActions && isNestedProjectsOn && (
          <EditProjectAction workspaceId={workspaceId} project={project} />
        ),
        showEditActions && onRename && (
          <RenameProjectAction onRename={onRename} />
        ),
      ]
        .filter(isTruthy)
        .map((action, index) => (
          <React.Fragment key={index}>
            {index === 0 && showDivider && <Dropdown.Divider />}
            {action}
          </React.Fragment>
        ))}
      {showDivider && <Dropdown.Divider />}
      {showLeaveAction && (
        <LeaveProjectAction
          workspaceIdentifier={workspaceId}
          projectIdentifier={project.identifier}
          projectName={project.name}
        />
      )}
      {showEditActions &&
        (isNestedProjectsOn ? (
          <Dropdown.Item
            onClick={() =>
              showModal(MoveStorageItemModal, {
                item: project,
                workspaceIdentifier: workspaceId,
                parentProjectIdentifier: project.parentProjectIdentifier,
                originType: 'stashed',
              })
            }
          >
            Unarchive&hellip;
          </Dropdown.Item>
        ) : (
          <Dropdown.Item
            onClick={() => {
              showModal(UnarchiveProjectModal, {
                projectIdentifier: project.identifier,
                projectName: project.name,
                workspaceIdentifier: workspaceId,
              })
            }}
          >
            Unarchive&hellip;
          </Dropdown.Item>
        ))}
      {showEditActions && (
        <DeleteProjectAction project={project} workspaceId={workspaceId} />
      )}
    </>
  )
}
